const VENUE_TO_MIC = {
  "London Stock Exchange": "XLON",
  "NASDAQ - All Markets": "XNAS",
  "NASDAQ Global Select Market": "XNAS",
  "NASDAQ Capital Market": "XNAS",
  "NYSE": "XNYS",
  "NYSE American": "XNYS",
  "NYSE Arca": "ARCX",
  "BATS": "BATS",
  "GTSM": "XNAS",
  "Drivewealth": null,
  "Other OTC": null,
};

const ISIN_PREFIX_TO_MIC = {
  US: "XNAS",
  GB: "XLON",
  DE: "XETR",
  FR: "XPAR",
  NL: "XAMS",
  IT: "XMIL",
  ES: "XMAD",
  SE: "XSTO",
  FI: "XHEL",
  NO: "XOSL",
  DK: "XCOP",
  PT: "XLIS",
  BE: "XBRU",
  CH: "XZUR",
  AT: "XWBO",
};

const REQUIRED_COLUMNS = [
  "Title", "Type", "Timestamp", "Buy / Sell", "Ticker", "ISIN", "Quantity", "Venue",
];

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const SAFE_FILENAME_RE = /^[a-zA-Z0-9 _().-]+$/;
const TICKER_RE = /^[A-Z0-9.-]{1,20}$/i;
const ALLOWED_MIME_TYPES = new Set(["text/csv", "", "application/vnd.ms-excel"]);

export function sanitizeString(str) {
  if (typeof str !== "string") return "";
  return str
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/^[=+\-@\t\r]+/, "")
    .trim();
}

export function validateFile(file) {
  if (!file) return { valid: false, error: "No file selected." };

  if (!/\.csv$/i.test(file.name)) {
    return { valid: false, error: "File must be a .csv file." };
  }

  if (!ALLOWED_MIME_TYPES.has(file.type)) {
    return { valid: false, error: "Invalid file type. Please upload a CSV file." };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: "File exceeds the 5 MB size limit." };
  }

  const nameWithoutExt = file.name.replace(/\.csv$/i, "");
  if (!SAFE_FILENAME_RE.test(nameWithoutExt)) {
    return { valid: false, error: "Filename contains invalid characters." };
  }

  return { valid: true };
}

export function mapVenueToMIC(venue, isin) {
  if (VENUE_TO_MIC[venue] !== undefined && VENUE_TO_MIC[venue] !== null) {
    return VENUE_TO_MIC[venue];
  }
  const prefix = typeof isin === "string" ? isin.slice(0, 2).toUpperCase() : "";
  return ISIN_PREFIX_TO_MIC[prefix] || "XNAS";
}

function splitCsvRow(line) {
  const fields = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      fields.push(field);
      field = "";
    } else {
      field += ch;
    }
  }
  fields.push(field);
  return fields;
}

function optFloat(raw) {
  if (raw === undefined || raw === null) return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function optDate(raw) {
  if (!raw || !raw.trim()) return null;
  const ts = Date.parse(raw.trim());
  return Number.isFinite(ts) ? ts : null;
}

function field(fields, colIndex, col) {
  const idx = colIndex[col];
  return idx !== undefined ? (fields[idx] || "") : "";
}

/**
 * Parses a Freetrade activity export CSV.
 *
 * Returns:
 *   allTransactions  – every parseable row (all types), rich structure for
 *                      future features such as retroactive dividend calculation
 *   buyTransactions  – subset of ORDER/BUY rows used to populate stock holdings
 *   summary          – { total, buys, tickers }
 *
 * Transaction shape (fields are null when not applicable to that type):
 *   {
 *     source,           // always "freetrade"
 *     type,             // ORDER | DIVIDEND | INTEREST | INTEREST_FROM_CASH | TOP_UP | …
 *     timestamp,        // ms since epoch
 *     name,             // Title column
 *     ticker,           // null for cash events
 *     isin,
 *     accountCurrency,
 *     totalAmount,      // Total Amount in Account Currency
 *     // ORDER fields
 *     buySell,          // "BUY" | "SELL" | null
 *     exchange,         // MIC code, null for non-ORDER
 *     quantity,
 *     pricePerShare,    // in account currency
 *     stampDuty,
 *     venue,
 *     orderId,
 *     instrumentCurrency,
 *     fxRate,
 *     // DIVIDEND / INTEREST fields
 *     dividendExDate,
 *     dividendPayDate,
 *     dividendEligibleQuantity,  // shares held on ex-date
 *     dividendAmountPerShare,
 *     dividendGrossAmount,
 *     dividendNetAmount,
 *     dividendWithheldTaxPct,
 *     dividendWithheldTaxAmount,
 *   }
 */
export function parseFreetrade(text) {
  if (typeof text !== "string" || !text.trim()) {
    return { allTransactions: [], buyTransactions: [], summary: { total: 0, buys: 0, tickers: 0 } };
  }

  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) {
    return { allTransactions: [], buyTransactions: [], summary: { total: 0, buys: 0, tickers: 0 } };
  }

  const headers = splitCsvRow(lines[0]);
  const colIndex = {};
  headers.forEach((h, i) => { colIndex[h.trim()] = i; });

  for (const col of REQUIRED_COLUMNS) {
    if (colIndex[col] === undefined) {
      throw new Error(`Missing required column: "${col}". Is this a Freetrade activity export?`);
    }
  }

  const allTransactions = [];
  const buyTransactions = [];

  for (let i = 1; i < lines.length; i++) {
    const f = splitCsvRow(lines[i]);
    const get = (col) => sanitizeString(field(f, colIndex, col));

    const type = get("Type");
    if (!type) continue;

    const timestamp = optDate(field(f, colIndex, "Timestamp"));
    if (timestamp === null) continue;

    const ticker = get("Ticker") || null;
    const isin = get("ISIN") || null;
    const venue = get("Venue") || null;
    const buySell = get("Buy / Sell") || null;

    const tx = {
      source: "freetrade",
      type,
      timestamp,
      name: get("Title") || null,
      // Normalise Freetrade ticker quirks: strip trailing lowercase ("MCp"→"MC")
      // then trailing dot ("BA."→"BA") so stored tickers match Yahoo Finance base symbols.
      ticker: ticker && TICKER_RE.test(ticker) ? (ticker.replace(/[a-z]+$/, "").replace(/\.$/, "") || null) : null,
      isin,
      accountCurrency: get("Account Currency") || null,
      totalAmount: optFloat(field(f, colIndex, "Total Amount in Account Currency")),
      // ORDER fields
      buySell,
      exchange: null,
      quantity: optFloat(field(f, colIndex, "Quantity")),
      pricePerShare: optFloat(field(f, colIndex, "Price per Share in Account Currency")),
      stampDuty: optFloat(field(f, colIndex, "Stamp Duty")),
      venue,
      orderId: get("Order ID") || null,
      instrumentCurrency: get("Instrument Currency") || null,
      fxRate: optFloat(field(f, colIndex, "FX Rate")),
      // Dividend / interest fields — populated for DIVIDEND and INTEREST types
      dividendExDate: optDate(field(f, colIndex, "Dividend Ex Date")),
      dividendPayDate: optDate(field(f, colIndex, "Dividend Pay Date")),
      dividendEligibleQuantity: optFloat(field(f, colIndex, "Dividend Eligible Quantity")),
      dividendAmountPerShare: optFloat(field(f, colIndex, "Dividend Amount Per Share")),
      dividendGrossAmount: optFloat(field(f, colIndex, "Dividend Gross Distribution Amount")),
      dividendNetAmount: optFloat(field(f, colIndex, "Dividend Net Distribution Amount")),
      dividendWithheldTaxPct: optFloat(field(f, colIndex, "Dividend Withheld Tax Percentage")),
      dividendWithheldTaxAmount: optFloat(field(f, colIndex, "Dividend Withheld Tax Amount")),
    };

    // Derive exchange for ORDER rows with a valid ticker
    if (type === "ORDER" && tx.ticker && venue && isin) {
      tx.exchange = mapVenueToMIC(venue, isin);
    }

    allTransactions.push(tx);

    // buyTransactions: ORDER rows with a valid ticker and positive quantity only
    if (
      type === "ORDER" &&
      buySell === "BUY" &&
      tx.ticker !== null &&
      tx.quantity !== null &&
      tx.quantity > 0 &&
      tx.exchange !== null
    ) {
      buyTransactions.push(tx);
    }
  }

  const uniqueTickers = new Set(buyTransactions.map((t) => t.ticker)).size;
  return {
    allTransactions,
    buyTransactions,
    summary: { total: allTransactions.length, buys: buyTransactions.length, tickers: uniqueTickers },
  };
}
